import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, X } from "lucide-react";
import { api, money, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { deliverFile } from "@/lib/pdfFiling";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "Monthly" },
  { key: "quarter", label: "Quarterly" },
  { key: "year", label: "Yearly" },
  { key: "custom", label: "Custom range" },
];

export default function StaffTakings() {
  const { branch } = useAuth();
  const [period, setPeriod] = useState("today");
  const [fromDate, setFromDate] = useState(shopToday());
  const [toDate, setToDate] = useState(shopToday());
  const [allBranches, setAllBranches] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  const params = useCallback(
    () => ({
      period,
      ...(period === "custom" ? { from_date: fromDate, to_date: toDate } : {}),
      ...(allBranches ? {} : { branch }),
    }),
    [period, fromDate, toDate, allBranches, branch]
  );

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/reports/staff", { params: params() })
      .then(({ data }) => setData(data))
      .catch((err) =>
        toast.error(
          (err.response?.data?.detail && String(err.response.data.detail)) ||
            "Could not build the staff report"
        )
      )
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const openStaff = async (staff) => {
    try {
      const { data } = await api.get("/reports/staff/transactions", {
        params: { ...params(), staff },
      });
      setDrill(data);
    } catch (e) {
      toast.error("Could not load those transactions");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/reports/staff.csv", {
        params: params(),
        responseType: "blob",
      });
      await deliverFile(
        res.data,
        "Reports",
        `staff-takings-${data?.from_date}-to-${data?.to_date}.csv`,
        toast
      );
    } catch (e) {
      toast.error("Could not download the CSV");
    }
  };

  const methods = data?.methods || [];
  const t = data?.totals;

  return (
    <div className="fade-up" data-testid="staff-takings">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
        {allBranches ? "All branches" : branch}
      </p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Staff Takings</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        What each person put through and how the money came in — use the cash column to
        reconcile the till at close.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-2" data-testid="staff-filters">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriod(p.key)}
            className={`border px-4 py-2 text-sm transition-colors duration-150 ${
              period === p.key
                ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                : "border-border hover:bg-muted"
            }`}
            data-testid={`staff-period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
        {period === "custom" && (
          <>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                From
              </Label>
              <Input type="date" className="mt-1 h-10 w-[160px] rounded-none" value={fromDate}
                     onChange={(e) => setFromDate(e.target.value)} data-testid="staff-from-date" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                To
              </Label>
              <Input type="date" className="mt-1 h-10 w-[160px] rounded-none" value={toDate}
                     onChange={(e) => setToDate(e.target.value)} data-testid="staff-to-date" />
            </div>
          </>
        )}
        <label className="flex items-center gap-2 px-2 text-sm">
          <input type="checkbox" checked={allBranches}
                 onChange={(e) => setAllBranches(e.target.checked)}
                 data-testid="staff-all-branches" />
          Both stores
        </label>
        <Button variant="outline" className="rounded-none" onClick={downloadCsv}
                data-testid="staff-csv-button">
          <Download className="mr-1.5 h-4 w-4" /> CSV
        </Button>
      </div>

      {data && (
        <p className="mt-4 text-xs uppercase tracking-wider text-muted-foreground" data-testid="staff-range">
          {data.from_date} to {data.to_date}
        </p>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["Money collected", money(t?.collected ?? 0), "staff-total-collected"],
          ["Cash taken", money(t?.methods?.CASH ?? 0), "staff-total-cash"],
          ["Card + Afterpay", money((t?.methods?.CARD ?? 0) + (t?.methods?.AFTERPAY ?? 0)), "staff-total-card"],
          ["Invoiced", money(t?.invoiced ?? 0), "staff-total-invoiced"],
        ].map(([label, value, testid]) => (
          <div key={testid} className="border border-border p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-2 text-2xl font-bold" data-testid={testid}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto border border-border">
        <table className="w-full min-w-[1100px] text-sm" data-testid="staff-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold">Staff</th>
              <th className="px-3 py-2.5 text-right font-semibold">Records</th>
              <th className="px-3 py-2.5 text-right font-semibold">Invoiced</th>
              <th className="px-3 py-2.5 text-right font-semibold">Collected</th>
              {methods.map((m) => (
                <th key={m} className="px-3 py-2.5 text-right font-semibold">
                  {m === "BANK TRANSFER" ? "Bank" : m.charAt(0) + m.slice(1).toLowerCase()}
                </th>
              ))}
              <th className="px-3 py-2.5 text-right font-semibold">Platform handling fees</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(!data?.rows || data.rows.length === 0) && (
              <tr>
                <td colSpan={6 + methods.length} className="px-3 py-12 text-center text-muted-foreground">
                  {loading ? "Building report…" : "No activity in this period."}
                </td>
              </tr>
            )}
            {(data?.rows || []).map((r) => (
              <tr
                key={r.staff}
                className="cursor-pointer hover:bg-[#FBF8F2]"
                onClick={() => openStaff(r.staff)}
                data-testid={`staff-row-${r.staff}`}
              >
                <td className="px-3 py-2.5 font-semibold">
                  {r.staff}
                  <div className="text-xs font-normal text-muted-foreground">
                    {r.invoices} inv · {r.bullion} bullion · {r.contracts} contract ·{" "}
                    {r.repairs_booked} repair
                    {r.voids ? ` · ${r.voids} void` : ""}
                  </div>
                </td>
                <td className="tabular px-3 py-2.5 text-right">
                  {r.invoices + r.bullion + r.contracts + r.loans + r.repairs_booked}
                </td>
                <td className="tabular px-3 py-2.5 text-right">{money(r.invoiced)}</td>
                <td className="tabular px-3 py-2.5 text-right font-bold">{money(r.collected)}</td>
                {methods.map((m) => (
                  <td key={m} className="tabular px-3 py-2.5 text-right" data-testid={`staff-${r.staff}-${m}`}>
                    {r.methods[m] ? money(r.methods[m]) : "-"}
                  </td>
                ))}
                <td className="tabular px-3 py-2.5 text-right">{money(r.fees)}</td>
              </tr>
            ))}
            {data?.rows?.length > 0 && (
              <tr className="bg-[#FBF8F2] font-bold" data-testid="staff-totals-row">
                <td className="px-3 py-2.5">All staff</td>
                <td className="tabular px-3 py-2.5 text-right">
                  {t.invoices + t.bullion + t.contracts + t.loans + t.repairs_booked}
                </td>
                <td className="tabular px-3 py-2.5 text-right">{money(t.invoiced)}</td>
                <td className="tabular px-3 py-2.5 text-right">{money(t.collected)}</td>
                {methods.map((m) => (
                  <td key={m} className="tabular px-3 py-2.5 text-right">
                    {t.methods[m] ? money(t.methods[m]) : "-"}
                  </td>
                ))}
                <td className="tabular px-3 py-2.5 text-right">{money(t.fees)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {drill && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
             data-testid="staff-drill-dialog">
          <div className="my-8 w-full max-w-3xl border border-border bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-bold md:text-lg">{drill.staff}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {drill.totals.count} records · {money(drill.totals.total)} invoiced ·{" "}
                  {money(drill.totals.collected)} collected
                </p>
              </div>
              <button type="button" onClick={() => setDrill(null)} data-testid="staff-drill-close"
                      aria-label="Close">
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="mt-4 max-h-[60vh] overflow-y-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-[#0A0A0A] text-white">
                  <tr>
                    {["Date", "Reference", "Type", "Customer", "Paid by", "Total", "Collected"].map(
                      (h) => (
                        <th key={h} className={`px-3 py-2 font-semibold ${
                          ["Total", "Collected"].includes(h) ? "text-right" : "text-left"
                        }`}>
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {drill.rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                        Nothing in this period.
                      </td>
                    </tr>
                  )}
                  {drill.rows.map((r, i) => (
                    <tr key={i} data-testid={`staff-drill-row-${i}`}>
                      <td className="tabular px-3 py-2 text-xs">
                        {r.date} {r.time}
                      </td>
                      <td className="px-3 py-2 font-semibold">
                        {r.kind === "repair" ? r.reference : `#${r.reference}`}
                      </td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">
                        {r.kind.replace("_", " ")}
                        {r.status === "void" ? " (void)" : ""}
                      </td>
                      <td className="px-3 py-2">{r.customer_name}</td>
                      <td className="px-3 py-2 text-xs">{r.methods || "-"}</td>
                      <td className="tabular px-3 py-2 text-right">{money(r.total)}</td>
                      <td className="tabular px-3 py-2 text-right">{money(r.collected)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
